// File: src/components/admin/analysis-tasks/TaskScheduleTab.js
// Schedule tab for analysis task detail view — shows all user schedules for this task
// Enhanced with reconciliation controls (Issue #81)

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Skeleton,
    Alert,
    Button,
    TextField,
    InputAdornment,
    Snackbar,
} from '@mui/material';
import {
    Visibility as ViewIcon,
    Refresh as RefreshIcon,
    Sync as ReconcileIcon,
    Pause as PauseIcon,
    PlayArrow as ResumeIcon,
    FlashOn as TriggerIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';
import { formatRelativeTime, formatFutureTime } from '../../../utils/scheduleUtils';
import UserSchedulerDetailDialog from '../scheduler/UserSchedulerDetailDialog';

const TaskScheduleTab = ({ taskId, task }) => {
    const [schedules, setSchedules] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [selectedUserUid, setSelectedUserUid] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [reconciling, setReconciling] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchSchedules = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await adminApi.getTaskScheduleDetails(taskId, { search });
            setSchedules(result.schedules || []);
            setSummary(result.summary || null);
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [taskId, search]);

    useEffect(() => {
        fetchSchedules();
    }, [fetchSchedules]);

    const handleReconcile = async () => {
        setReconciling(true);
        try {
            const report = await adminApi.reconcileTask(taskId);
            setSnackbar({
                open: true,
                severity: 'success',
                message: `Reconciliation complete: +${report.added} added, -${report.removed} removed, ~${report.updated} updated${report.errors?.length ? `, ${report.errors.length} errors` : ''}`,
            });
            fetchSchedules();
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setSnackbar({ open: true, severity: 'error', message: `Reconciliation failed: ${message}` });
        } finally {
            setReconciling(false);
        }
    };

    const handlePause = async (uid) => {
        try {
            await adminApi.pauseTaskUserSchedule(taskId, uid);
            setSnackbar({ open: true, severity: 'success', message: `Schedule paused for ${uid}` });
            fetchSchedules();
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setSnackbar({ open: true, severity: 'error', message });
        }
    };

    const handleResume = async (uid) => {
        try {
            await adminApi.resumeTaskUserSchedule(taskId, uid);
            setSnackbar({ open: true, severity: 'success', message: `Schedule resumed for ${uid}` });
            fetchSchedules();
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setSnackbar({ open: true, severity: 'error', message });
        }
    };

    const handleTrigger = async (uid) => {
        try {
            await adminApi.triggerTaskUserSchedule(taskId, uid);
            setSnackbar({ open: true, severity: 'success', message: `Execution triggered for ${uid}` });
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setSnackbar({ open: true, severity: 'error', message });
        }
    };

    const handleViewUser = (uid) => {
        setSelectedUserUid(uid);
        setDetailDialogOpen(true);
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'active': return <Chip label="active" size="small" color="success" />;
            case 'paused': return <Chip label="paused" size="small" color="warning" />;
            case 'admin_paused': return <Chip label="admin paused" size="small" color="warning" variant="outlined" />;
            case 'error': return <Chip label="circuit broken" size="small" color="error" />;
            case 'disabled': return <Chip label="disabled" size="small" variant="outlined" />;
            default: return <Chip label={status} size="small" variant="outlined" />;
        }
    };

    // Show appropriate state message
    if (!task?.scheduleConfig) {
        return (
            <Paper sx={{ p: 3 }}>
                <Alert severity="info">
                    Configure a schedule trigger on this task to enable automatic scheduling.
                </Alert>
            </Paper>
        );
    }

    if (task?.status !== 'active') {
        return (
            <Paper sx={{ p: 3 }}>
                <Alert severity="info">
                    Activate the task to begin automatic scheduling. Current status: <strong>{task?.status}</strong>
                </Alert>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 3 }}>
            {/* Summary bar */}
            {summary && (
                <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                        <strong>{summary.totalScheduled}</strong> users scheduled
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <strong>{summary.executionsLast7d}</strong> executions (7d)
                    </Typography>
                    {summary.lastReconciliation && (
                        <Typography variant="body2" color="text.secondary">
                            Last reconciled: {formatRelativeTime(summary.lastReconciliation.timestamp)}
                        </Typography>
                    )}
                </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                    User Schedules
                </Typography>
                <TextField
                    size="small"
                    placeholder="Search by email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ width: 220 }}
                />
                <Button
                    startIcon={<ReconcileIcon />}
                    onClick={handleReconcile}
                    disabled={reconciling}
                    size="small"
                    variant="outlined"
                    color="primary"
                >
                    {reconciling ? 'Reconciling...' : 'Reconcile Now'}
                </Button>
                <Button
                    startIcon={<RefreshIcon />}
                    onClick={fetchSchedules}
                    disabled={loading}
                    size="small"
                >
                    Refresh
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Interval</TableCell>
                            <TableCell>Last Run</TableCell>
                            <TableCell>Next Run</TableCell>
                            <TableCell>Failures</TableCell>
                            <TableCell>Schedule ID</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from({ length: 3 }).map((_, index) => (
                                <TableRow key={index}>
                                    {Array.from({ length: 8 }).map((__, ci) => (
                                        <TableCell key={ci}><Skeleton /></TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : schedules.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">
                                        {search ? 'No matching users found' : '0 users match the current audience rules'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            schedules.map((schedule) => (
                                <TableRow
                                    key={schedule.uid}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => handleViewUser(schedule.uid)}
                                >
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={500}>
                                            {schedule.email !== 'unknown' ? schedule.email : schedule.uid}
                                        </Typography>
                                        {schedule.email !== 'unknown' && (
                                            <Typography variant="caption" color="text.secondary">
                                                {schedule.uid}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {getStatusChip(schedule.status)}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {schedule.schedule?.interval || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatRelativeTime(schedule.schedule?.lastRun)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatFutureTime(schedule.schedule?.nextRun)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            color={(schedule.schedule?.consecutiveFailures || 0) >= 5 ? 'error' : 'inherit'}
                                        >
                                            {schedule.schedule?.consecutiveFailures || 0}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                            {schedule.scheduleId || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                        {schedule.status === 'active' && (
                                            <Tooltip title="Pause">
                                                <IconButton size="small" onClick={() => handlePause(schedule.uid)}>
                                                    <PauseIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {(schedule.status === 'admin_paused' || schedule.status === 'paused') && (
                                            <Tooltip title="Resume">
                                                <IconButton size="small" onClick={() => handleResume(schedule.uid)}>
                                                    <ResumeIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Trigger Now">
                                            <IconButton size="small" onClick={() => handleTrigger(schedule.uid)}>
                                                <TriggerIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="View User Scheduler">
                                            <IconButton size="small" onClick={() => handleViewUser(schedule.uid)}>
                                                <ViewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {schedules.length} schedule(s) found across all users
            </Typography>

            {/* User Detail Dialog */}
            <UserSchedulerDetailDialog
                open={detailDialogOpen}
                onClose={() => { setDetailDialogOpen(false); setSelectedUserUid(null); }}
                userUid={selectedUserUid}
                onRefresh={fetchSchedules}
            />

            {/* Snackbar for action feedback */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default TaskScheduleTab;
